import { useState, useEffect } from 'react';
import { invokeRapidHealthCheckAPI } from '../../api/healthCheckAPI';

const ALL_TABS = ["clusterDetails", "infrastructure", "rapidPods", "configuration", "enabledModules", "bandwidthTest"];

// --- GLOBAL MEMORY (Persists outside the React lifecycle) ---
let globalFetchId = 0;
let isFetchingGlobal = false;
let globalTabData = JSON.parse(sessionStorage.getItem('audit_tab_data') || '{}');

export function useClusterAudit() {
  const [allCustomers, setAllCustomers] = useState([]);
  
  // Helper to safely retrieve from session storage
  const getSessionValue = (key, defaultValue) => {
    const saved = sessionStorage.getItem(key);
    if (!saved) return defaultValue;
    try {
      return JSON.parse(saved);
    } catch {
      return saved;
    }
  };

  // --- PERSISTENT STATES ---
  const [selectedCustomerName, setSelectedCustomerName] = useState(() => 
    getSessionValue('audit_selected_customer_name', "")
  );
  const [availableClusters, setAvailableClusters] = useState(() => 
    getSessionValue('audit_available_clusters', [])
  );
  const [selectedCustomer, setSelectedCustomer] = useState(() => 
    getSessionValue('audit_selected_customer', null)
  );
  const [activeTab, setActiveTab] = useState(() => 
    getSessionValue('audit_active_tab', "")
  );
  const [lastSynced, setLastSynced] = useState(() => 
    getSessionValue('audit_last_synced', null)
  );
  const [selectedTabs, setSelectedTabsState] = useState(() => 
    getSessionValue('audit_selected_tabs_config', ALL_TABS)
  );
  
  const [tabData, setTabData] = useState(globalTabData);
  const [isInitialLoading, setIsInitialLoading] = useState(isFetchingGlobal);

  // --- SYNC TO SESSION STORAGE ---
  useEffect(() => {
    sessionStorage.setItem('audit_selected_customer_name', JSON.stringify(selectedCustomerName));
    sessionStorage.setItem('audit_available_clusters', JSON.stringify(availableClusters));
    sessionStorage.setItem('audit_selected_customer', JSON.stringify(selectedCustomer));
    sessionStorage.setItem('audit_active_tab', JSON.stringify(activeTab));
    sessionStorage.setItem('audit_last_synced', JSON.stringify(lastSynced));
    sessionStorage.setItem('audit_selected_tabs_config', JSON.stringify(selectedTabs));
  }, [selectedCustomerName, availableClusters, selectedCustomer, activeTab, lastSynced, selectedTabs]);

  // --- BACKGROUND DATA POLLING ---
  // If the user navigates back while a fetch is in progress, this keeps the local state
  // in sync with the global object that is still receiving API responses.
  useEffect(() => {
    let interval;
    if (isInitialLoading) {
      interval = setInterval(() => {
        setTabData({...globalTabData});
      }, 500);
    }
    return () => clearInterval(interval);
  }, [isInitialLoading]);

  // Load the customers from the JSON file
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const response = await fetch('./customers.json');
        const data = await response.json();
        setAllCustomers(data.customers);
      } catch (err) {
        console.error("Failed to load customers.json:", err);
      }
    };
    loadCustomers();
  }, []);

  const resetAuditState = () => {
    globalTabData = {};
    sessionStorage.setItem('audit_tab_data', '{}');
    setTabData({});
    setActiveTab("");
    setLastSynced(null);
    globalFetchId++; // Effectively cancels any ongoing fetch loops
  };

  const handleNameChange = (name) => {
    setSelectedCustomerName(name);
    const clusters = allCustomers.filter(c => c.name === name);
    setAvailableClusters(clusters);
    setSelectedCustomer(null);
    resetAuditState();
  };

  const handleClusterChange = (e) => {
    const clusterName = e.target.value;
    const customer = allCustomers.find(c => 
      c.name === selectedCustomerName && c.clusterName === clusterName
    );
    setSelectedCustomer(customer);
    resetAuditState();
  };

  // Wrapper for updating selected tabs that also resets the current view
  const setSelectedTabs = (newTabList) => {
    setSelectedTabsState(newTabList);
    resetAuditState();
  };

  const runFullAudit = async () => {
    if (!selectedCustomer || selectedTabs.length === 0) return;

    setIsInitialLoading(true);
    isFetchingGlobal = true;
    
    globalTabData = {}; 
    setTabData({});
    setLastSynced(null); 
    setActiveTab(selectedTabs[0]); // Default to first selected tab
    
    const fetchId = ++globalFetchId;

    try {
      for (const tabName of selectedTabs) {
        // Exit loop if a new audit has been started or state was reset
        if (fetchId !== globalFetchId) break;
        
        try {
          const res = await invokeRapidHealthCheckAPI(
            selectedCustomer.id, 
            selectedCustomer.clusterId, 
            tabName
          );
          const resData = res || {};
          
          // Update global memory object (lives through unmounts)
          globalTabData[tabName] = resData[tabName] || {};
          
          // Write to storage immediately for persistence
          sessionStorage.setItem('audit_tab_data', JSON.stringify(globalTabData));
          
          // Update local state for active UI feedback
          setTabData({...globalTabData});
          setLastSynced(new Date().toLocaleTimeString());

        } catch (err) { 
          console.error(`Error fetching tab ${tabName}:`, err); 
        }
      }
    } catch (err) {
      console.error("Audit sequence failed:", err);
    } finally {
      // Only set loading to false if this is still the active fetch sequence
      if (fetchId === globalFetchId) {
        setIsInitialLoading(false);
        isFetchingGlobal = false;
      }
    }
  };

  return {
    allCustomers,
    EXPECTED_TABS: ALL_TABS,
    selectedTabs,
    setSelectedTabs,
    selectedCustomerName,
    availableClusters,
    selectedCustomer,
    tabData,
    activeTab,
    setActiveTab,
    isInitialLoading,
    lastSynced,
    handleNameChange,
    handleClusterChange,
    runFullAudit
  };
}